import { Button } from 'react-bootstrap';
import './queueTable.css';
import ColorLabel from '../../../components/common/ColorLabel';
import PhoenixBadge from '../../../components/base/Badge';
const getNiceStep = max => {
  // slider ergonomics
  if (max <= 200) return 1;
  if (max <= 1000) return 5;
  if (max <= 5000) return 10;
  if (max <= 20000) return 25;
  return 50;
};
const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const U = v =>
  String(v ?? '')
    .trim()
    .toUpperCase();
const fmt = n => Number(n || 0).toLocaleString();

const keyOf = r => `${r?.orderId ?? ''}::${r?.lineId ?? ''}`;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const RangeWithFill = ({ value, min = 0, max = 100, step = 1, onChange }) => {
  const v = clamp(toNum(value), min, max);
  const pct = max > min ? ((v - min) / (max - min)) * 100 : 0;

  return (
    <input
      type="range"
      className="qs-range m-0"
      min={min}
      max={max}
      step={step}
      value={v}
      onChange={onChange}
      style={{
        background: `linear-gradient(to right,
          var(--qs-accent) 0%,
          var(--qs-accent) ${pct}%,
          var(--qs-track) ${pct}%,
          var(--qs-track) 100%)`
      }}
    />
  );
};
const QueueTable = ({
  queue,
  onChangeLength,
  onRemove,
  onReorder,
  onClear,
  onSimulate,
  onLaunch,
  title = 'Simulation Queue',
  subtitle = 'List order = launch order (row 1 enters the wizard + queue first)',
  launchLabel = 'Launch'
}) => {
  if (!queue?.length) return null;

  const handleSimulate = () =>
    onSimulate ? onSimulate(queue) : console.log('SIMULATE', queue);

  const handleLaunch = () =>
    onLaunch ? onLaunch(queue) : console.log('LAUNCH', queue);

  return (
    <div className="mt-4 border rounded-2">
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
        <div>
          <div className="fw-semi-bold">{title}</div>
          <div className="text-muted fs-10">{subtitle}</div>
        </div>

        <div className="d-flex gap-2">
          <Button
            size="sm"
            className="qs-btn qs-btn-sim"
            onClick={handleSimulate}
          >
            Simulate
          </Button>

          <Button
            size="sm"
            className="qs-btn qs-btn-launch"
            onClick={handleLaunch}
          >
            {launchLabel}
          </Button>

          <Button
            size="sm"
            className="qs-btn qs-btn-clear"
            onClick={() => onClear?.()}
            title="Clear queue"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>Client</th>
              <th>Cable</th>
              <th className="text-center">Spec</th>
              <th className="text-end">Remaining (m)</th>
              <th className="text-end" style={{ width: 520 }}>
                Launch length (m)
              </th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>

          <tbody>
            {queue.map((q, idx) => {
              const k = keyOf(q);
              const max = Math.max(0, toNum(q.remainingToPlan));
              const step = getNiceStep(max);
              const current = clamp(toNum(q.launchLength ?? 0), 0, max);

              const remainingAfter = clamp(max - current, 0, max);

              const setPercent = pct => {
                const v = Math.round(max * pct);
                onChangeLength(k, String(clamp(v, 0, max)));
              };

              const handleInput = raw => {
                const v = clamp(toNum(raw), 0, max);
                onChangeLength(k, String(v));
              };

              return (
                <tr key={k}>
                  <td className="text-muted">
                    <div className="d-flex align-items-center gap-1">
                      <span className="fw-semi-bold">{idx + 1}</span>
                      {onReorder && (
                        <div className="d-flex flex-column" style={{ lineHeight: 0.7 }}>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-body"
                            style={{ fontSize: 10, lineHeight: 1 }}
                            disabled={idx === 0}
                            title="Move up"
                            onClick={() => onReorder(idx, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-body"
                            style={{ fontSize: 10, lineHeight: 1 }}
                            disabled={idx === queue.length - 1}
                            title="Move down"
                            onClick={() => onReorder(idx, 1)}
                          >
                            ▼
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {q.client ? (
                      <PhoenixBadge variant="phoenix" bg="primary" className="fs-10">
                        {U(q.client)}
                      </PhoenixBadge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semi-bold">{q.cableName}</span>
                      <ColorLabel colorName={q.color} />
                    </div>
                  </td>

                  <td className="text-center">{q.spec}</td>

                  <td className="text-end fw-bold">
                    {fmt(max)}
                    <div className="text-muted fs-10">
                      after launch: {fmt(remainingAfter)}
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-3">
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <RangeWithFill
                            min={0}
                            max={max}
                            step={step}
                            value={current}
                            onChange={e => handleInput(e.target.value)}
                          />
                        </div>

                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          value={current}
                          min={0}
                          max={max}
                          step={1}
                          onChange={e => handleInput(e.target.value)}
                          style={{ width: 160 }}
                        />
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm qs-pill qs-pill-25"
                            onClick={() => setPercent(0.25)}
                          >
                            25%
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm qs-pill qs-pill-50"
                            onClick={() => setPercent(0.5)}
                          >
                            50%
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm qs-pill qs-pill-100"
                            onClick={() => setPercent(1)}
                          >
                            100%
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm qs-pill qs-pill-max"
                            onClick={() => handleInput(max)}
                            title="Set to max"
                          >
                            Max
                          </button>
                        </div>

                        <div className="text-muted fs-10">
                          step: {step}m • max: {fmt(max)}m
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="text-end">
                    <Button
                      size="sm"
                      className="qs-btn qs-btn-remove"
                      onClick={() => onRemove(k)}
                      title="Remove item"
                    >
                      ✕
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QueueTable;
